<template>
  <section class="flex flex-col md:flex-row w-full">
    <!-- LEFT: fixed width on desktop, full width on mobile -->
    <div class="hidden md:block w-96 flex-shrink-0">
      <div class="sidecard-base">
        <div class="pt-2">
          <h1 class="text-2xl font-semibold text-primaryPalette">Today's Celebration</h1>
        </div>
        <div v-if="!renderCurrentLoading" class="py-4">
          <ul v-if="itemTodayDetails.length">
            <li
              v-for="(item, index) in itemTodayDetails"
              :key="index"
              class="bg-white shadow-sm my-4 p-4 sm:p-5 rounded-md"
            >
              <div v-if="item.celebrationType === 'birthday'" class="flex items-center gap-2">
                <CakeSvg class="w-4 h-4 sm:w-5 sm:h-5" />
                <div class="flex flex-col">
                  <span class="text-sm sm:text-base text-primaryPaletteLight font-medium">{{
                    item.name
                  }}</span>
                  <span class="text-xs sm:text-sm text-primarylightdark">
                    Celebrating {{ item.gender === 'M' ? 'his' : 'her' }} birthday
                  </span>
                </div>
              </div>

              <div v-else class="flex items-center gap-2">
                <PartySvg class="w-4 h-4 sm:w-5 sm:h-5" />
                <div class="flex flex-col">
                  <span class="text-sm sm:text-base text-primaryPaletteLight font-medium">{{
                    item.name
                  }}</span>
                  <span class="text-xs sm:text-sm text-primarylightdark">
                    Celebrating {{ item.gender === 'M' ? 'his' : 'her' }}
                    {{ renderYearsInService(item.yearsOfService) }}
                  </span>
                </div>
              </div>
            </li>
          </ul>
          <div
            v-else
            class="text-primarylightdark text-sm flex flex-col items-center justify-center py-1 space-y-3"
          >
            <ArchiveSvg class="w-6 h-6" />
            <h2 class="text-sm sm:text-base">There are currently no celebrations</h2>
          </div>
        </div>
        <div v-else class="flex justify-center items-center pt-6">
          <LoadingGlobal />
        </div>
      </div>
    </div>

    <!-- RIGHT: takes remaining width on desktop, full width on mobile -->
    <div class="flex-1 md:pl-6">
      <!-- TABS -->
      <div
        class="inline-flex w-full md:w-auto rounded-md border border-gray-200 shadow-sm overflow-hidden bg-bgInactive mb-6 md:mb-0"
      >
        <button @click="activeTab = 'birthday'" :class="tabClass('birthday')">Birthday</button>
        <button @click="activeTab = 'anniversary'" :class="tabClass('anniversary')">
          Anniversary
        </button>
        <button @click="activeTab = 'attendance'" :class="tabClass('attendance')">
          Best in Attendance
        </button>
      </div>

      <!-- CONTENT -->
      <div class="py-7">
        <!-- Birthday Tab -->
        <ul v-if="activeTab === 'birthday'" class="space-y-6">
          <div v-if="!renderLoading">
            <div class="py-1 pb-6">
              <h1 class="text-xl font-medium text-primaryInactiveLight">
                Birthday Celebrants for This Month
              </h1>
            </div>
            <ul class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
              <li
                v-for="(item, index) in itemDetails"
                :key="index"
                class="bg-stone-50 rounded-md shadow-sm flex flex-col items-center justify-center py-4 px-2 sm:px-4"
              >
                <img
                  :src="item.mediaLink"
                  alt="profile"
                  class="w-20 h-20 sm:w-28 sm:h-28 object-cover [clip-path:circle(50%)]"
                />
                <div
                  class="pt-2 sm:pt-3 flex flex-col items-center justify-center text-primarydark text-center"
                >
                  <CakeSvg class="w-4 h-4 sm:w-5 sm:h-5" />
                  <div class="space-y-1 p-1">
                    <h2 class="text-sm sm:text-lg font-medium text-primaryPalette">
                      {{ item.name }}
                    </h2>
                    <h2 class="text-xs sm:text-sm">{{ renderMonthDateFormat(item.birthDate) }}</h2>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div v-else class="flex justify-center items-center">
            <LoadingGlobal />
          </div>
        </ul>

        <!-- Anniversary Tab -->
        <ul v-else-if="activeTab === 'anniversary'" class="space-y-6">
          <div v-if="!renderLoading">
            <div class="py-1 pb-6">
              <h1 class="text-xl font-medium text-primaryInactiveLight">
                Anniversary Celebrants for This Month
              </h1>
            </div>
            <ul class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
              <li
                v-for="(item, index) in itemDetails"
                :key="index"
                class="bg-stone-50 rounded-md shadow-sm flex flex-col items-center justify-center py-4 px-2 sm:px-4"
              >
                <img
                  :src="item.mediaLink"
                  alt="profile"
                  class="w-20 h-20 sm:w-28 sm:h-28 object-cover [clip-path:circle(50%)]"
                />
                <div
                  class="pt-2 sm:pt-3 flex flex-col items-center justify-center text-primarydark text-center"
                >
                  <PartySvg class="w-4 h-4 sm:w-5 sm:h-5" />
                  <div class="space-y-1 p-1">
                    <h2 class="text-sm sm:text-lg font-medium text-primaryPalette">
                      {{ item.name }}
                    </h2>
                    <h2 class="text-xs sm:text-sm">
                      {{ renderYearsInService(item.yearsOfService) }}
                    </h2>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div v-else class="flex justify-center items-center">
            <LoadingGlobal />
          </div>
        </ul>

        <!-- Attendance Tab -->
        <ul v-else class="space-y-6">
          <div v-if="!renderLoading">
            <div v-if="itemRecognition.length">
              <ul class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                <li
                  v-for="(item, index) in itemRecognition"
                  :key="index"
                  class="bg-stone-50 rounded-md shadow-sm flex flex-col items-center justify-center py-4 px-2 sm:px-4"
                >
                  <img
                    :src="item.employee_info.mediaLink"
                    alt="profile"
                    class="w-20 h-20 sm:w-28 sm:h-28 object-cover [clip-path:circle(50%)]"
                  />
                  <div
                    class="pt-2 sm:pt-3 flex flex-col items-center justify-center text-primarydark text-center"
                  >
                    <PartySvg class="w-4 h-4 sm:w-5 sm:h-5" />
                    <div class="space-y-1 p-1">
                      <h2 class="text-sm sm:text-lg font-medium text-primaryPalette">
                        {{ item.employee_info.name }}
                      </h2>
                      <h2 class="text-xs sm:text-sm">
                        {{ renderFullMonthFormat(item.month_of_recognition) }}
                      </h2>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <h1 v-else class="text-base font-medium text-primaryInactiveLight text-center">
              There is no award for Best Attendance.
            </h1>
          </div>
          <div v-else class="flex justify-center items-center">
            <LoadingGlobal />
          </div>
        </ul>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, inject, watch } from 'vue';
import CakeSvg from '@/components/svgs/CakeSvg.vue';
import PartySvg from '@/components/svgs/PartySvg.vue';
import ArchiveSvg from '@/components/svgs/ArchiveSvg.vue';
import reusableBase from '@/utils/reusables/dateFormat';

export default {
  components: { CakeSvg, PartySvg, ArchiveSvg },
  setup() {
    const { renderMonthDateFormat, renderYearsInService, renderFullMonthFormat } = reusableBase();
    const axios = inject('$axios');

    const activeTab = ref('birthday');
    const itemDetails = ref([]);
    const itemTodayDetails = ref([]);
    const itemRecognition = ref([]);
    const renderLoading = ref(true);
    const renderCurrentLoading = ref(true);

    const tabClass = (tab) => {
      const isActive = activeTab.value === tab;
      return [
        'px-8 py-3 text-base font-medium transition-all duration-200',
        isActive
          ? 'bg-[#FFF7E6] text-orange-500'
          : 'bg-bgInactive text-primaryInactiveLight hover:text-gray-600'
      ].join(' ');
    };

    const currentMonthItemRender = async () => {
      try {
        const result = await axios.get(
          `https://urchin-app-t57nb.ondigitalocean.app/api/gazette/getEmployees/celebration`,
          {
            params: { type: activeTab.value, filter: 'currentmonth' }
          }
        );
        itemDetails.value = result.data || [];
      } catch (error) {
        console.log('currentMonthItemRender', error);
        itemDetails.value = [];
      } finally {
        renderLoading.value = false;
      }
    };

    const todayCelebration = async () => {
      try {
        const result = await axios.get(
          `https://urchin-app-t57nb.ondigitalocean.app/api/gazette/getEmployees/celebration`,
          {
            params: { type: 'all', filter: 'today' }
          }
        );
        itemTodayDetails.value = result.data || [];
      } catch (error) {
        console.log('todayCelebration', error);
        itemTodayDetails.value = [];
      } finally {
        renderCurrentLoading.value = false;
      }
    };

    const recognitionHandler = async () => {
      try {
        const result = await axios.get(`api/recognitions`);
        itemRecognition.value = result.data?.data || [];
      } catch (error) {
        console.log('recognitionHandler', error);
        itemRecognition.value = [];
      }
    };

    onMounted(async () => {
      await todayCelebration();
      await currentMonthItemRender();
      await recognitionHandler();
    });

    watch(activeTab, async () => {
      renderLoading.value = true;
      await currentMonthItemRender();
    });

    return {
      activeTab,
      tabClass,
      itemDetails,
      itemTodayDetails,
      itemRecognition,
      renderMonthDateFormat,
      renderYearsInService,
      renderFullMonthFormat,
      renderLoading,
      renderCurrentLoading
    };
  }
};
</script>
